// diferenças principais: items-center, min-h por size, shrink-0 e left offset por size
const cx = (...c) => c.filter(Boolean).join(" ");

export default function CustomSwitch({
  checked = false,
  onChange,
  disabled = false,
  label,
  subtitle,
  size = "md",
  id,
  className,
  showIcons = true,
  color = "blue",
}) {
  const sizes = {
    sm: {
      track: "w-10 h-5",
      thumb: "w-4 h-4",
      translate: "translate-x-5",
      pad: "p-[2px]",
      minH: "min-h-[20px]",
      left: "left-[2px]",
    },
    md: {
      track: "w-12 h-6",
      thumb: "w-5 h-5",
      translate: "translate-x-5",
      pad: "p-[3px]",
      minH: "min-h-[24px]",
      left: "left-[3px]",
    },
    lg: {
      track: "w-14 h-7",
      thumb: "w-6 h-6",
      translate: "translate-x-4",
      pad: "p-1",
      minH: "min-h-[28px]",
      left: "left-[4px]",
    },
  };
  const s = sizes[size] || sizes.md;

  const activeBg =
    {
      blue: "from-blue-600 to-indigo-600",
      emerald: "from-emerald-600 to-teal-600",
      violet: "from-violet-600 to-fuchsia-600",
      rose: "from-rose-600 to-pink-600",
    }[color] || "from-blue-600 to-indigo-600";

  const ringColor =
    {
      blue: "ring-blue-500 dark:ring-blue-400",
      emerald: "ring-emerald-500 dark:ring-emerald-400",
      violet: "ring-violet-500 dark:ring-violet-400",
      rose: "ring-rose-500 dark:ring-rose-400",
    }[color] || "ring-blue-500 dark:ring-blue-400";

  return (
    <label
      htmlFor={id}
      className={cx(
        // alinhamento vertical centralizado
        "flex items-center gap-3 select-none",
        s.minH, // garante altura mínima do container = altura do track
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className
      )}
    >
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        aria-label={typeof label === "string" ? label : undefined}
        onClick={() => !disabled && onChange?.(!checked)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onChange?.(!checked);
          }
        }}
        className={cx(
          "relative rounded-full transition-all outline-none shrink-0", // <- impede “esticamento”
          "focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-0",
          `focus-visible:${ringColor}`,
          s.track,
          s.pad,
          "backdrop-blur-sm border border-black/5 dark:border-white/10",
          !disabled && "hover:brightness-[1.03]",
          checked
            ? `bg-gradient-to-r ${activeBg}`
            : "bg-gray-200 dark:bg-gray-700"
        )}
      >
        <span
          className={cx(
            "absolute -inset-1 rounded-full pointer-events-none transition-opacity",
            checked ? "opacity-40" : "opacity-0",
            {
              blue: "bg-blue-500/20",
              emerald: "bg-emerald-500/20",
              violet: "bg-violet-500/20",
              rose: "bg-rose-500/20",
            }[color] || "bg-blue-500/20"
          )}
          aria-hidden="true"
        />

        <span
          className={cx(
            "absolute inset-0 rounded-full pointer-events-none",
            "bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,255,255,.25),transparent_55%)]",
            "opacity-40"
          )}
          aria-hidden="true"
        />

        {showIcons && (
          <>
            <span
              className={cx(
                "absolute left-1 top-1/2 -translate-y-1/2 text-[10px] font-semibold",
                checked ? "opacity-100" : "opacity-0",
                "text-white transition-opacity"
              )}
            >
              ✓
            </span>
            <span
              className={cx(
                "absolute right-1 top-1/2 -translate-y-1/2 text-[10px] font-semibold",
                checked ? "opacity-0" : "opacity-70",
                "text-gray-600 dark:text-gray-300 transition-opacity"
              )}
            >
              •
            </span>
          </>
        )}

        {/* thumb centralizado com offset por size */}
        <span
          className={cx(
            "relative z-[1] block rounded-full bg-white dark:bg-gray-100",
            "shadow-[0_2px_6px_rgba(0,0,0,0.25)]",
            "transition-transform duration-300 ease-out will-change-transform",
            "top-1/2 -translate-y-1/2", // garante centralização vertical
            s.left, // offset inicial consistente
            s.thumb,
            checked ? s.translate : "-translate-x-1"
          )}
        >
          <span
            className="pointer-events-none absolute inset-0 rounded-full bg-white/50 mix-blend-overlay"
            aria-hidden="true"
          />
        </span>
      </button>

      {(label || subtitle) && (
        <div className="leading-tight">
          {label && (
            <div className="text-sm md:text-[15px] text-gray-900 dark:text-gray-100">
              {label}
            </div>
          )}
          {subtitle && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {subtitle}
            </div>
          )}
        </div>
      )}
    </label>
  );
}
